import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { useEditWriteGuard } from '../../../EditSession/useEditWriteGuard';

export interface KeepEventPayload {
    eventId: string;
    keepEvent: boolean;
}

interface KeepEventRequestBody {
    keep_event: boolean;
    user_id: string;
}

interface KeepEventResponse {
    event_id: string;
    plan_id: string;
    keep_event: boolean;
    promotions_affected: number;
    promotion_ids_changed: string[];
    status: string;
    message: string;
}

const setKeepEvent = async (eventId: string, body: KeepEventRequestBody): Promise<KeepEventResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.keepEvent(eventId), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        data: body,
    });
    return response.data;
};

const useKeepEventMutation = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
    const queryClient = useQueryClient();
    const assertCanWrite = useEditWriteGuard();

    const { mutate, isPending } = useMutation({
        mutationFn: ({ eventId, keepEvent }: KeepEventPayload) => {
            assertCanWrite();
            return setKeepEvent(eventId, {
                keep_event: keepEvent,
                user_id: localStorage.getItem('email') ?? '',
            });
        },
        onSuccess: (data, { eventId }) => {
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            // The write cascades onto every promo of the event, so the event
            // detail view is stale too, not just the calendar rows.
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.eventDetails(eventId) });
            // The response reports how many promotions the cascade touched, which
            // is the part the user cannot see from the row they acted on.
            sendNotification('success', data?.message || 'Event successfully updated');
            onSuccess?.();
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                'Failed to update event';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useKeepEventMutation;
